/**
 * The typed half of the studio: exact cue times, the ending, BPM and key.
 *
 * The waveform is for finding a moment by ear; this is for stating it. Every
 * marker has a box that takes a typed time to the millisecond, a button that
 * drops it at the playhead, and a label saying whether a person or the analyser
 * put it there -- a detected Cue In and a curated one look the same on a
 * waveform and are not the same thing.
 *
 * **Quick buttons for the corrections that actually happen.** A tempo estimate
 * that is wrong is almost always wrong by a ratio: double, half, or the 3:4 of a
 * shuffle felt in the wrong pulse. Those are one click each. Tapping is for the
 * rest.
 */

import { useEffect, useRef, useState } from "react";
import type { CueField, Ending, Track } from "../types";
import { CAMELOT_WHEEL, lookupKey, type KeyNaming } from "../format/keys";
import type { KeyNotation } from "../format/notation";
import { MARKERS, formatTime, parseTime, type CueValues } from "./cueMath";

const RATIOS: { label: string; factor: number; title: string }[] = [
  { label: "×2", factor: 2, title: "Double — Nova heard the half-time pulse" },
  { label: "÷2", factor: 0.5, title: "Halve — Nova heard the double-time pulse" },
  { label: "×¾", factor: 0.75, title: "Three quarters — a triplet feel counted as the beat" },
  { label: "×4⁄3", factor: 4 / 3, title: "Four thirds — the beat counted as a triplet feel" },
];

/** Short musical spelling for the picker's labels: `Am`, `F#`, `Bb`. Display only. */
export function keyTag(k: KeyNaming): string {
  const root = k.root.replace(/♭/g, "b").replace(/♯/g, "#");
  return k.mode === "minor" ? `${root}m` : root;
}

function TimeBox({
  value,
  onCommit,
  invalid,
}: {
  value: number | null;
  onCommit: (t: number | null) => void;
  invalid?: boolean;
}) {
  const [text, setText] = useState(formatTime(value));
  const [editing, setEditing] = useState(false);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (!editing) setText(value == null ? "" : formatTime(value));
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    // Focus and leave without typing is not an edit. Committing the displayed text
    // would round a detected 211.93249 s to the millisecond and call that a change.
    if (text === (value == null ? "" : formatTime(value))) {
      setBad(false);
      return;
    }
    if (text.trim() === "") {
      setBad(false);
      onCommit(null);
      return;
    }
    const t = parseTime(text);
    if (t == null) {
      setBad(true);
      return;
    }
    setBad(false);
    onCommit(t);
  };

  return (
    <input
      className={`time-box ${bad || invalid ? "bad" : ""}`}
      value={text}
      placeholder="—"
      spellCheck={false}
      onFocus={(e) => {
        setEditing(true);
        e.currentTarget.select();
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(value == null ? "" : formatTime(value));
          setBad(false);
          setEditing(false);
          e.currentTarget.blur();
          e.stopPropagation();
        }
      }}
      title="m:ss.mmm, or seconds. Empty returns it to what Nova detected."
    />
  );
}

export function MetadataForm({
  track,
  values,
  sources,
  ending,
  selectedField,
  notation,
  bpm,
  keyCode,
  onSelectField,
  onField,
  onPlace,
  onEnding,
  onBpm,
  onKey,
}: {
  track: Track;
  values: CueValues;
  sources: Partial<Record<CueField, "set" | "detected" | null>>;
  ending: Ending | null;
  selectedField: CueField | null;
  notation: KeyNotation;
  /** BPM in force, including an unsaved change. */
  bpm: number | null;
  /** Camelot code in force, including an unsaved change. */
  keyCode: string | null;
  onSelectField: (field: CueField) => void;
  onField: (field: CueField, t: number | null) => void;
  onPlace: (field: CueField) => void;
  onEnding: (ending: Ending | null) => void;
  onBpm: (bpm: number | null) => void;
  onKey: (camelot: string | null) => void;
}) {
  const [bpmText, setBpmText] = useState("");
  const taps = useRef<number[]>([]);
  const [tapCount, setTapCount] = useState(0);

  useEffect(() => {
    setBpmText(bpm == null ? "" : String(Math.round(bpm * 10) / 10));
  }, [bpm]);
  useEffect(() => {
    taps.current = [];
    setTapCount(0);
  }, [track.id]);

  const tap = () => {
    const now = performance.now();
    const prior = taps.current;
    // A pause longer than two seconds starts a new count; nobody taps a
    // 30 BPM record, and a stale tap would drag the average down.
    const fresh = prior.length && now - prior[prior.length - 1] > 2000 ? [now] : [...prior, now].slice(-9);
    taps.current = fresh;
    setTapCount(fresh.length);
    if (fresh.length >= 4) {
      const span = fresh[fresh.length - 1] - fresh[0];
      onBpm(Math.round((60000 / (span / (fresh.length - 1))) * 10) / 10);
    }
  };

  const introLength =
    values.intro != null ? values.intro - (values.cueIn ?? 0) : null;
  const hookLength =
    values.hookStart != null && values.hookEnd != null ? values.hookEnd - values.hookStart : null;
  const detectedKey = lookupKey(track.detectedKey ?? null);
  const candidates = new Set((track.keyCandidates ?? []).map((c) => c.value));

  return (
    <div className="meta-form">
      <div className="section-label">
        Cue points <em>· I T [ ] O drop at playhead</em>
      </div>
      <div className="cue-rows">
        {MARKERS.map(({ field, label, color, key }) => {
          const source = sources[field];
          const missingCritical = (field === "cueIn" || field === "cueOut") && values[field] == null;
          return (
            <div
              key={field}
              className={`cue-row ${selectedField === field ? "selected" : ""}`}
              style={{ ["--marker" as string]: color }}
              onMouseDown={() => onSelectField(field)}
            >
              <span className="cue-swatch" />
              <span className="cue-label">
                {label}
                {missingCritical && <span className="badge critical">missing</span>}
              </span>
              <TimeBox value={values[field]} onCommit={(t) => onField(field, t)} />
              <span
                className={`cue-source ${source ?? "none"}`}
                title={
                  source === "set"
                    ? "Set by a person."
                    : source === "detected"
                      ? "Detected by Nova."
                      : "Not placed"
                }
              >
                {source === "set" ? "set" : source === "detected" ? "auto" : "—"}
              </span>
              <button
                className="mini"
                title={`Drop ${label} at the playhead (${key})`}
                onClick={() => onPlace(field)}
              >
                ⊕
              </button>
              <button
                className="mini"
                title="Return to what Nova detected"
                disabled={values[field] == null}
                onClick={() => onField(field, null)}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <div className="cue-lengths">
        <span className="intro-len" title="From the start point to where the vocal comes in">
          Intro {introLength == null ? "—" : formatTime(introLength)}
        </span>
        <span title="Highlight length">Highlight {hookLength == null ? "—" : formatTime(hookLength)}</span>
      </div>

      <div className="section-label">
        Ending <em>· C cold · F fade</em>
      </div>
      <div className="segmented">
        {(
          [
            ["cold", "Cold end", "Stops dead. It will not be faded."],
            ["fade", "Fade out", "Fades naturally. It may be faded at the mix out."],
          ] as const
        ).map(([value, label, title]) => (
          <button
            key={value}
            className={`seg ${ending === value ? "on" : ""} ${value}`}
            title={title}
            onClick={() => onEnding(ending === value ? null : value)}
          >
            {label}
          </button>
        ))}
        {ending == null && <span className="badge advisory">not set</span>}
      </div>

      <div className="section-label">
        BPM
        <em>
          {" "}· Nova reads {track.detectedBpm != null ? Math.round(track.detectedBpm * 10) / 10 : "nothing"}
          {track.bpmSource === "curator" && " · yours"}
        </em>
      </div>
      <div className="bpm-row">
        <input
          className="bpm-box"
          value={bpmText}
          inputMode="decimal"
          spellCheck={false}
          onChange={(e) => setBpmText(e.target.value)}
          onBlur={() => {
            const n = Number(bpmText.replace(",", "."));
            if (bpmText.trim() === "") return;
            if (Number.isFinite(n) && n >= 30 && n <= 300) onBpm(Math.round(n * 10) / 10);
            else setBpmText(bpm == null ? "" : String(bpm));
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
        {RATIOS.map((r) => (
          <button
            key={r.label}
            className="quick"
            title={r.title}
            disabled={bpm == null}
            onClick={() => bpm != null && onBpm(Math.round(bpm * r.factor * 10) / 10)}
          >
            {r.label}
          </button>
        ))}
        <button className="quick tap" onClick={tap} title="Tap along to the beat; four taps or more">
          Tap{tapCount > 0 && tapCount < 4 ? ` ${tapCount}` : ""}
        </button>
      </div>
      {track.bpmCandidates && track.bpmCandidates.length > 1 && (
        <div className="candidates">
          {track.bpmCandidates.slice(0, 5).map((c) => (
            <button key={c.value} className="chip" onClick={() => onBpm(Number(c.value))}>
              {c.value}
            </button>
          ))}
        </div>
      )}

      <div className="section-label">
        Key
        <em>
          {" "}· Nova reads{" "}
          {detectedKey
            ? notation === "camelot"
              ? `${detectedKey.camelot} (${detectedKey.musical})`
              : `${detectedKey.musical} (${detectedKey.camelot})`
            : "nothing"}
          {track.keySource === "curator" && " · yours"}
        </em>
      </div>
      <div className="key-picker" role="listbox" aria-label="Key">
        {CAMELOT_WHEEL.map((k) => (
          <button
            key={k.camelot}
            role="option"
            aria-selected={keyCode === k.camelot}
            className={`key-pick ${k.mode} ${keyCode === k.camelot ? "on" : ""} ${
              candidates.has(k.camelot) ? "candidate" : ""
            }`}
            title={`${k.camelot} · ${k.musical}${candidates.has(k.camelot) ? " · one of Nova's candidates" : ""}`}
            onClick={() => onKey(k.camelot)}
          >
            {notation === "camelot" ? k.camelot : keyTag(k)}
          </button>
        ))}
      </div>
    </div>
  );
}
